import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  Plus,
  FileText,
  Sparkles,
  ArrowRight,
  FolderKanban,
  Palette,
  CheckCircle2,
  LayoutTemplate,
  Trash2,
} from "lucide-react";
import { useProposals, usePipelineStats, useDeleteProposal } from "../../hooks/useProposals";

export function ProposalDashboard() {
  const { data: proposals = [], isLoading } = useProposals();
  const { data: stats } = usePipelineStats();
  const deleteProposal = useDeleteProposal();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-amber-400/80">
              Proposal Builder
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">
              Proposals
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-white/45">
              Create customer-specific proposals from your reusable templates.
              Brand Setup controls company-wide defaults. Templates define the
              reusable blueprint. Proposals are the final customer-facing output.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/proposal-builder/new"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
            >
              <Plus className="h-4 w-4" />
              New proposal
            </Link>

            <Link
              to="/proposal-builder/templates"
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-2.5 text-sm font-medium text-white/80 transition-colors hover:bg-white/[0.06]"
            >
              <FolderKanban className="h-4 w-4" />
              Templates
            </Link>
          </div>
        </div>

        <div className="mb-8 grid gap-4 lg:grid-cols-4">
          <QuickCard
            icon={<Palette className="h-5 w-5 text-amber-400" />}
            title="Brand Setup"
            body="Company-wide branding, cover letter, reusable pages, service catalog, and builder foundation."
            cta="Open brand setup"
            to="/proposal-builder/setup"
          />
          <QuickCard
            icon={<LayoutTemplate className="h-5 w-5 text-amber-400" />}
            title="Templates"
            body="Reusable proposal blueprints for client types, services, pages, builder mode, and fixed-page direction."
            cta="Open templates"
            to="/proposal-builder/templates"
          />
          <QuickCard
            icon={<FileText className="h-5 w-5 text-amber-400" />}
            title="New Proposal"
            body="Create a customer-specific proposal from a selected template and review it before saving."
            cta="Start proposal"
            to="/proposal-builder/new"
          />
          <QuickCard
            icon={<Sparkles className="h-5 w-5 text-amber-400" />}
            title="Workflow"
            body="Brand Setup → Template → Proposal → PDF, send, and sign."
            cta="Build from templates"
            to="/proposal-builder/templates"
          />
        </div>

        <div className="mb-8 grid gap-4 md:grid-cols-4">
          <StatCard label="Total" value={String(stats?.total ?? 0)} />
          <StatCard label="Draft" value={String(stats?.draft ?? 0)} />
          <StatCard label="Sent" value={String(stats?.sent ?? 0)} />
          <StatCard
            label="Signed value"
            value={formatCurrency(stats?.signed_value ?? 0)}
          />
        </div>

        <div className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <p className="text-xs uppercase tracking-[0.14em] text-white/35">
            Current module flow
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <FlowCard
              title="1. Brand Setup"
              body="Set branding, reusable pages, service catalog, and builder foundation."
            />
            <FlowCard
              title="2. Templates"
              body="Choose defaults for services, pages, AI context, builder mode, and fixed pages."
            />
            <FlowCard
              title="3. New Proposal"
              body="Pick a template, add client details, describe the project, and review."
            />
            <FlowCard
              title="4. Proposal Detail"
              body="Preview PDF, send to client, and track sign / approval status."
            />
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03]">
          <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-white/55">
                Recent proposals
              </h2>
              <p className="mt-1 text-sm text-white/35">
                Customer-facing proposals created from your templates.
              </p>
            </div>

            <Link
              to="/proposal-builder/new"
              className="text-sm text-white/45 transition-colors hover:text-white/70"
            >
              New proposal
            </Link>
          </div>

          {isLoading ? (
            <div className="px-6 py-10 text-sm text-white/35">
              Loading proposals...
            </div>
          ) : proposals.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-lg font-semibold text-white/70">
                No proposals yet
              </p>
              <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-white/35">
                Start from a template and create your first customer-specific
                proposal.
              </p>

              <div className="mt-5 flex items-center justify-center gap-3">
                <Link
                  to="/proposal-builder/new"
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
                >
                  <Plus className="h-4 w-4" />
                  Create proposal
                </Link>

                <Link
                  to="/proposal-builder/templates"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-2.5 text-sm font-medium text-white/80 transition-colors hover:bg-white/[0.06]"
                >
                  <FolderKanban className="h-4 w-4" />
                  View templates
                </Link>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {proposals.map((proposal) => (
                <div key={proposal.id} className="group flex items-center gap-2 px-6 py-4 transition-colors hover:bg-white/[0.03]">
                  <Link
                    to="/proposal-builder/$id"
                    params={{ id: proposal.id }}
                    className="flex min-w-0 flex-1 items-center justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold text-white/85">
                          {proposal.prospect_name}
                        </p>
                        <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/20 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-white/35">
                          <CheckCircle2 className="h-3 w-3" />
                          {proposal.status}
                        </span>
                      </div>

                      <p className="mt-1 text-xs text-white/35">
                        {formatCurrency(Number(proposal.total_value ?? 0))}
                      </p>
                    </div>

                    <ArrowRight className="h-4 w-4 shrink-0 text-white/25" />
                  </Link>

                  {confirmDeleteId === proposal.id ? (
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          deleteProposal.mutate(proposal.id, {
                            onSettled: () => setConfirmDeleteId(null),
                          });
                        }}
                        disabled={deleteProposal.isPending}
                        className="rounded-lg px-2.5 py-1 text-xs font-medium text-red-400 transition-colors hover:bg-red-500/10"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="rounded-lg px-2.5 py-1 text-xs text-white/40 transition-colors hover:bg-white/[0.05]"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(proposal.id)}
                      className="shrink-0 rounded-lg p-2 text-white/20 opacity-0 transition-all hover:bg-red-500/10 hover:text-red-400 group-hover:opacity-100"
                      aria-label="Delete proposal"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuickCard({
  icon,
  title,
  body,
  cta,
  to,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  cta: string;
  to:
    | "/proposal-builder/setup"
    | "/proposal-builder/templates"
    | "/proposal-builder/new";
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-amber-500/20 bg-amber-500/10">
        {icon}
      </div>

      <h2 className="mt-4 text-lg font-semibold text-white">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-white/40">{body}</p>

      <Link
        to={to}
        className="mt-4 inline-flex items-center gap-2 text-sm text-white/55 transition-colors hover:text-white/80"
      >
        {cta}
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function FlowCard({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <p className="text-sm font-medium text-white/80">{title}</p>
      <p className="mt-2 text-sm leading-6 text-white/40">{body}</p>
    </div>
  );
}

function StatCard({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <p className="text-xs uppercase tracking-[0.14em] text-white/35">
        {label}
      </p>
      <p className="mt-3 text-xl font-semibold text-white/85">{value}</p>
    </div>
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}